// ============= Imports =============
import { AskSession } from '@/apis/ask/askHistory/types';
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {};

// ============= Types =============

export interface GetUserWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
}

export type WorkspaceType = 'Private' | 'Collaborative' | 'Standard';

export interface WorkspaceDataRoom {
  workspaceDataRoomFiles: unknown[];
  workspaceDataRoomCollections: unknown[];
  workspaceCentralDataStores: unknown[];
}

export type WorkspaceUsers = Record<string, boolean>;

export interface UserWorkspace {
  id: string;
  _etag: string;
  title: string;
  description: string;
  createdOn: string;
  matterNumber: string | null;
  matterName: string | null;
  matterCode?: string | null;
  clientName: string | null;
  clientId: string | null;
  clientCode?: string | null;
  creatorId: string;
  workspaceType: WorkspaceType;
  askSessions: AskSession[];
  workflows: unknown[];
  dataRoom: WorkspaceDataRoom;
  workspaceUsers: WorkspaceUsers;
  lastUpdate: string;
  markedForDeletion: string | null;
  deletionProcessStarted: string | null;
}

export type UserWorkspaceResponse = UserWorkspace[];

export interface WorkspaceTimeInfo {
  time: string;
  title: string;
  id: string;
  workspaceType: WorkspaceType;
}

// ============= Utility Functions =============
/**
 * Formats a date string into a human-readable format
 * @param dateString ISO date string
 * @returns Formatted date string (e.g., "Jan 15, 2024 14:30")
 */
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Sorts workspaces by lastUpdate (most recent first) and returns formatted time, title, and id
 * @param workspaces Array of user workspaces
 * @returns Array of workspace info objects sorted by lastUpdate (descending)
 */
export const getWorkspacesOrderedByLastUpdate = (
  workspaces: UserWorkspaceResponse
): WorkspaceTimeInfo[] => {
  return workspaces
    .slice() // Create a copy to avoid mutating the original array
    .sort((a, b) => {
      const dateA = new Date(a.lastUpdate).getTime();
      const dateB = new Date(b.lastUpdate).getTime();
      return dateB - dateA; // Descending order (most recent first)
    })
    .map((workspace) => ({
      time: formatTime(workspace.lastUpdate),
      title: workspace.title,
      id: workspace.id,
      workspaceType: workspace.workspaceType,
    }));
};

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Fetches user workspace information
 * @param getAccessToken Function to acquire access token
 * @returns Promise with API response containing user workspace data or error
 */
export const getUserWorkspace = async ({
  getAccessToken,
}: GetUserWorkspaceProps): Promise<UserWorkspaceResponse> => {
  try {
    console.log('🔄 Fetching user workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/user`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ User workspace fetched successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error fetching user workspace:', error);
    throw error;
  }
};
